import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, Pause, Play } from "lucide-react";
import { GALLERY } from "@/data/content";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { Lightbox } from "./Lightbox";

const INTERVAL = 5000;

export const Gallery = () => {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!playing || active !== null) return;
    const id = setTimeout(() => setCurrent((c) => (c + 1) % GALLERY.length), INTERVAL);
    return () => clearTimeout(id);
  }, [playing, current, active]);

  const item = GALLERY[current];

  return (
    <section id="galeria" className="relative py-28 sm:py-36" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead eyebrow="Pillanatképek" title="Galéria" testId="gallery-head" />
          <Reveal delay={0.2}>
            <p className="font-mono text-xs tracking-wider text-white/40" data-testid="gallery-count">
              {String(GALLERY.length).padStart(2, "0")} kép — automatikus vetítés
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} y={60} className="mt-14">
          <div
            className="group relative aspect-[16/9] max-h-[78vh] w-full overflow-hidden rounded-[2rem] border border-white/[0.08] bg-[#111113] shadow-[0_50px_140px_-50px_rgba(0,0,0,0.95)]"
            data-testid="gallery-stage"
          >
            <AnimatePresence mode="sync">
              <motion.img
                key={item.src}
                src={item.src}
                alt={item.alt}
                data-testid="gallery-stage-image"
                initial={{ opacity: 0, scale: 1.0 }}
                animate={{ opacity: 1, scale: 1.1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 1.2, ease: EASE },
                  scale: { duration: INTERVAL / 1000 + 1.5, ease: "linear" },
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/90 via-[#0a0a0b]/10 to-transparent" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/40 to-transparent" />

            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-5 p-6 sm:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={item.src}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.6, ease: EASE }}
                >
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/40">
                    {String(current + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")}
                  </p>
                  <p className="mt-2 font-display text-xl font-bold text-white sm:text-3xl" data-testid="gallery-caption">
                    {item.alt}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlaying((p) => !p)}
                  data-testid="gallery-play-toggle"
                  aria-label={playing ? "Vetítés szüneteltetése" : "Vetítés indítása"}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
                >
                  {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                </button>
                <div className="flex flex-1 items-center gap-2">
                  {GALLERY.map((g, i) => (
                    <button
                      key={g.src}
                      type="button"
                      onClick={() => setCurrent(i)}
                      data-testid={`gallery-dot-${i}`}
                      aria-label={`${i + 1}. kép`}
                      className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/15"
                    >
                      {i === current && (
                        <motion.span
                          key={`${i}-${playing}`}
                          initial={{ scaleX: i < current ? 1 : 0 }}
                          animate={{ scaleX: playing ? 1 : 0.35 }}
                          transition={{ duration: playing ? INTERVAL / 1000 : 0.3, ease: "linear" }}
                          className="absolute inset-0 origin-left bg-[var(--glow)]"
                        />
                      )}
                      {i < current && <span className="absolute inset-0 bg-white/50" />}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setActive(current)}
                  data-testid="gallery-open-lightbox"
                  aria-label="Teljes képernyő"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
                >
                  <Maximize2 size={14} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-4 grid grid-cols-3 gap-4" data-testid="gallery-thumbs">
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.src}
              type="button"
              onClick={() => setCurrent(i)}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className={`relative aspect-[16/9] overflow-hidden rounded-2xl border transition-all duration-500 ${
                i === current ? "border-[rgba(94,177,255,0.6)] shadow-[0_0_30px_-6px_rgba(94,177,255,0.5)]" : "border-white/[0.08] opacity-50 hover:opacity-90"
              }`}
            >
              <img src={g.src} alt={g.alt} loading="lazy" className="h-full w-full object-cover" />
            </motion.button>
          ))}
        </div>
      </div>
      <Lightbox items={GALLERY} index={active} onClose={() => setActive(null)} onChange={setActive} />
    </section>
  );
};
